import {
  ChakraVStack,
  InputGroupV2,
  UniversalSelect,
  CheckboxV2,
  FireflyButton,
  FAIconName,
  Popover,
  TableFilter,
} from '@equip.health/ui';
import { useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  statusOptions,
  permissionOptions,
} from '~/lib/constants/user.constants';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import { getMultiSelectedUniversalSelectOptions } from '~/lib/util/profile.util';
import { useGetProviderTypesEnumQuery } from '~/store/services/enumerations';

interface Props {
  id: string;
  selectedFilters: TableFilter[];
  onSaveFilters: (filters: TableFilter[]) => void;
  isOpen: boolean;
  onClose: () => void;
}

const USER_STATUS = 'User status';
const UserFilterPopover = ({
  id,
  selectedFilters,
  onSaveFilters,
  isOpen,
  onClose,
}: Props) => {
  const { data: ProviderTypes } = useGetProviderTypesEnumQuery();
  const { providerTypePkTransition: isProviderTypePkTransitionEnabled } =
    useFlags<LDFlags>();

  const rolesMenuOptions = useMemo(
    () =>
      (ProviderTypes ?? []).map(({ label, id, code }) => ({
        label,
        value: isProviderTypePkTransitionEnabled ? code : id,
      })),
    [ProviderTypes, isProviderTypePkTransitionEnabled],
  );

  const getFilterValues = (key: string): FilterValue[] =>
    selectedFilters.find((f) => f.filterKey === key)?.filters ?? [];

  const [filters, setFilters] = useState<{
    role: FilterValue[];
    status: FilterValue[];
    permissions: FilterValue[];
  }>({
    permissions: getFilterValues('permissions'),
    role: getFilterValues('role'),
    status: getFilterValues('status'),
  });

  useEffect(() => {
    if (isOpen) {
      setFilters({
        permissions: getFilterValues('permissions'),
        role: getFilterValues('role'),
        status: getFilterValues('status'),
      });
    }
  }, [isOpen, selectedFilters]);

  const selectedPermissionValues = useMemo(
    () => new Set(filters.permissions.map((p) => p.value)),
    [filters.permissions],
  );

  const togglePermission = (permission: FilterValue, checked: boolean) => {
    setFilters((prev) => {
      const next = prev.permissions.filter((p) => p.value !== permission.value);
      return {
        ...prev,
        permissions: checked ? [...next, permission] : next,
      };
    });
  };

  const handleSave = () => {
    const newFilters = Object.entries(filters)
      .filter(([, value]) => value.length > 0)
      .map(([key, value]) => ({
        filterKey: key,
        filters: value,
        title:
          key === 'status' ? USER_STATUS : key[0].toUpperCase() + key.slice(1),
      }));

    if (newFilters.length) onSaveFilters(newFilters);
    onClose();
  };

  return (
    <Popover
      bodyContent={
        <ChakraVStack align="stretch" gap="12px">
          <InputGroupV2 id={`${id}__role`} label="Role">
            <UniversalSelect
              id={`${id}__role-select`}
              isSingleSelect={false}
              modalId={id}
              onChange={(selected) =>
                setFilters((prev) => ({
                  ...prev,
                  role:
                    selected?.map((s) => ({
                      label: s.label,
                      value: s.value,
                    })) ?? [],
                }))
              }
              options={rolesMenuOptions}
              value={getMultiSelectedUniversalSelectOptions(
                filters.role.map((r) => String(r.value)),
                rolesMenuOptions,
              )}
              withCheckBox
            />
          </InputGroupV2>
          <InputGroupV2 id={`${id}__status`} label="User status">
            <UniversalSelect
              id={`${id}__status-select`}
              isSingleSelect
              onChange={(selected) =>
                setFilters((prev) => ({
                  ...prev,
                  status: selected
                    ? [{ label: selected.label, value: selected.value }]
                    : [],
                }))
              }
              options={statusOptions}
              value={
                filters.status.length > 0
                  ? getSelectedUniversalSelectOption(
                      String(filters.status[0].value),
                      statusOptions,
                    )
                  : null
              }
            />
          </InputGroupV2>
          <InputGroupV2 id={`${id}__permissions`} label="Permissions">
            {permissionOptions.map((permission) => (
              <CheckboxV2
                id={`${id}__${permission.value}-permission`}
                isChecked={selectedPermissionValues.has(permission.value)}
                key={String(permission.value)}
                label={permission.label}
                onChange={(checked) => togglePermission(permission, checked)}
              />
            ))}
          </InputGroupV2>
          <FireflyButton
            id={`${id}__save-filter`}
            leftIcon={FAIconName.checkRegular}
            onClick={handleSave}
            variant="primary"
          >
            Save filters
          </FireflyButton>
        </ChakraVStack>
      }
      bodyPadding="24px"
      id={`${id}__userfilter-popover`}
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-end"
      size="md"
    />
  );
};

export default UserFilterPopover;
